<template>
  <v-container
    :class="hasActiveShift ? 'align-start' : 'align-center'"
    class="pa-1 pa-sm-2 fill-height"
    fluid
  >
    <v-row class="fill-height">
      <v-col
        v-if="hasActiveShift"
        cols="12"
      >
        <v-card>
          <v-card-title
            v-if="!posUser.eft_pos"
            class="pa-2"
          >
            <v-container
              class="pa-0"
              fluid
            >
              <v-row dense>
                <v-col
                  class="text-left"
                  cols="6"
                  sm="3"
                >
                  <div class="text-center">
                    <div class="text-caption pl-1 pt-0 blue--text">
                      <strong>{{ $t('Delivery.Common.ShiftStart') }}</strong>
                    </div>

                    <div class="text-h6 grey--text text--darken-1">
                      {{ deliveryStaffShift.ShiftStartMoney | currency }}
                    </div>

                    <div
                      class="text-caption pl-1 pt-1 grey--text"
                      style="white-space: nowrap;"
                    >
                      {{ deliveryStaffShift.ShiftStartDateTime.split(' ')[0] }}
                    </div>

                    <div class="text-caption">
                      {{ deliveryStaffShift.ShiftStartDateTime.split(' ')[1] }}
                    </div>
                  </div>
                </v-col>

                <v-col
                  class="text-left"
                  cols="6"
                  sm="3"
                >
                  <div
                    v-if="deliveryStaffShift.HasActiveShift"
                    class="text-center"
                  >
                    <div class="text-caption pl-1 pt-0 orange--text">
                      <strong>{{ $t('Delivery.Common.ShiftTotal') }}</strong>
                    </div>

                    <div class="text-h6 grey--text text--darken-1">
                      {{ getDeliveryStaffShiftTotals(true) | currency }}
                    </div>

                    <div
                      class="text-caption pl-1 pt-1"
                      style="white-space: nowrap;"
                    >
                      <span class="grey--text">
                        {{ $t('Delivery.Common.Orders') }} ({{ shiftOrders.length }})
                      </span>
                      <div>{{ getDeliveryStaffShiftTotals(false) | currency }}</div>
                    </div>
                  </div>
                </v-col>

                <v-col cols="12">
                  <v-divider />
                </v-col>
              </v-row>
            </v-container>
          </v-card-title>

          <v-card-text
            v-if="!posUser.eft_pos"
            class="pa-0"
          >
            <v-container
              class="pa-0"
              fluid
            >
              <v-row
                v-if="posUser.Settings.DeliveryStaff.Permissions.CanAddOrder || posUser.Settings.DeliveryStaff.Permissions.CanCloseShift"
                no-gutters
              >
                <v-col cols="12">
                  <v-subheader
                    class="grey--text text--darken-2 px-0"
                    style="height: 50px"
                  >
                    <v-btn
                      v-if="posUser.Settings.DeliveryStaff.Permissions.CanAddOrder"
                      :color="$t('Delivery.DeliveryStaffInfo.Button.AddOrder.Color')"
                      class="elevation-0 mx-1 px-2"
                      dark
                      small
                      @click="onAddOrderClick"
                    >
                      {{ $t('Delivery.DeliveryStaffInfo.Button.AddOrder.Title') }}
                    </v-btn>

                    <v-spacer />

                    <template
                      v-if="userIsDelivery && (tab === 'tab-received' || tab === 'tab-accepted') && (posUser.Settings.DeliveryStaff.Permissions.CanGenerateDocument || posUser.Settings.DeliveryStaff.Permissions.CanPrintDocument)"
                    >
                      <v-btn
                        v-if="posUser.Settings.DeliveryStaff.Permissions.CanGenerateDocument"
                        :disabled="!shiftTabOrdersForReceipt.length"
                        class="elevation-0 mx-1 px-2"
                        color="amber darken-1"
                        dark
                        small
                        @click="generateAllDocuments"
                      >
                        <span>{{ $t('Common.Button.IssueReceipts') }}</span>
                      </v-btn>

                      <!--
                      <v-btn
                        v-if="posUser.Settings.DeliveryStaff.Permissions.CanPrintDocument"
                        :disabled="!shiftTabOrdersForPrint.length"
                        class="elevation-0 mx-1 px-2"
                        color="blue"
                        dark
                        small
                        @click="printAllDocuments"
                      >
                        <span>{{ $t('Common.Button.PrintReceipts') }}</span>
                      </v-btn>
                      -->
                    </template>

                    <v-btn
                      v-if="posUser.Settings.DeliveryStaff.Permissions.CanCloseShift"
                      :color="$t('Delivery.Button.Shift.Close.Color')"
                      class="elevation-0 mx-2 px-2"
                      dark
                      small
                      @click="onShiftCloseClick"
                    >
                      {{ $t('Delivery.Button.Shift.Close.Title') }}
                    </v-btn>
                  </v-subheader>
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="12">
                  <v-tabs
                    v-model="tab"
                    grow
                    height="38"
                    show-arrows
                  >
                    <v-tab
                      class="px-2 text-caption"
                      tab-value="tab-added"
                      value="tab-added"
                    >
                      <v-badge
                        :value="!!shiftOrdersAdded.length"
                        color="red"
                        inline
                        left
                      >
                        <template #badge>
                          {{ shiftOrdersAdded.length }}
                        </template>

                        <span :class="{'ml-1': !!shiftOrdersAdded.length}">
                          {{ $t('Delivery.UserDashboard.Tab.Added.Title') }}
                        </span>
                      </v-badge>
                    </v-tab>

                    <v-tab
                      class="px-2 text-caption"
                      tab-value="tab-accepted"
                      value="tab-accepted"
                    >
                      <v-badge
                        :value="!!shiftOrdersAccepted.length"
                        color="orange"
                        inline
                        left
                      >
                        <template #badge>
                          {{ shiftOrdersAccepted.length }}
                        </template>

                        <span :class="{'ml-1': !!shiftOrdersAccepted.length}">
                          {{ $t('Delivery.UserDashboard.Tab.Accepted.Title') }}
                        </span>
                      </v-badge>
                    </v-tab>

                    <v-tab
                      class="px-2 text-caption"
                      tab-value="tab-received"
                      value="tab-received"
                    >
                      <v-badge
                        :value="!!shiftOrdersReceived.length"
                        color="purple"
                        inline
                        left
                      >
                        <template #badge>
                          {{ shiftOrdersReceived.length }}
                        </template>

                        <span :class="{'ml-1': !!shiftOrdersReceived.length}">
                          {{ $t('Delivery.UserDashboard.Tab.Received.Title') }}
                        </span>
                      </v-badge>
                    </v-tab>

                    <v-tab
                      class="px-2 text-caption"
                      tab-value="tab-completed"
                      value="tab-completed"
                    >
                      <v-badge
                        :value="!!shiftOrdersCompleted.length"
                        color="green"
                        inline
                        left
                      >
                        <template #badge>
                          {{ shiftOrdersCompleted.length }}
                        </template>

                        <span :class="{'ml-1': !!shiftOrdersCompleted.length}">
                          {{ $t('Delivery.UserDashboard.Tab.Completed.Title') }}
                        </span>
                      </v-badge>
                    </v-tab>
                  </v-tabs>
                </v-col>
              </v-row>

              <v-row
                class="pa-0 pb-2 overflow-x-hidden overflow-y-auto"
                no-gutters
                style="height: calc(100vh - 134px)"
              >
                <v-col cols="12">
                  <v-tabs-items
                    v-model="tab"
                    class="fill-height"
                  >
                    <v-tab-item
                      key="tab-added"
                      value="tab-added"
                    >
                      <delivery-user-orders-v-list
                        v-if="shiftOrdersAdded.length && tab === 'tab-added'"
                        :accept-action="true"
                        :orders="shiftOrdersAdded"
                        :remove-action="posUser.Settings.DeliveryStaff.Permissions.CanDeleteOrder"
                        dense
                        @accept-action-click="setShiftOrderStatus"
                        @remove-action-click="showDeleteOrderDialog"
                        @list-tile-click="getOrderDetails"
                        @update-orders="getDataUpdate"
                      />

                      <v-container
                        v-else
                        class="pa-6"
                        fluid
                      >
                        <v-row class="align-center justify-center">
                          <v-col
                            class="text-center grey--text"
                            cols="12"
                          >
                            <v-icon
                              class="mb-4"
                              color="grey lighten-2"
                              size="128"
                            >
                              {{ $t('Delivery.UserDashboard.Tab.Added.NoData.Icon') }}
                            </v-icon>
                            <div
                              class="text-h6 mb-2"
                              style="height: auto;"
                            >
                              {{ $t('Delivery.UserDashboard.Tab.Added.NoData.Title') }}
                            </div>
                            <div
                              class="text-subtitle-1"
                              style="height: auto;"
                            >
                              {{ $t('Delivery.UserDashboard.Tab.Added.NoData.Subtitle') }}
                            </div>
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-tab-item>

                    <v-tab-item
                      key="tab-accepted"
                      value="tab-accepted"
                    >
                      <delivery-user-orders-v-list
                        v-if="shiftOrdersAccepted.length && tab === 'tab-accepted'"
                        :generate-document-action="posUser.Settings.DeliveryStaff.Permissions.CanGenerateDocument"
                        :orders="shiftOrdersAccepted"
                        :print-document-action="posUser.Settings.DeliveryStaff.Permissions.CanPrintDocument"
                        :receive-action="true"
                        dense
                        @generate-document-action-click="generateDocument"
                        @print-document-action-click="printDocument"
                        @receive-action-click="setShiftOrderStatus"
                        @list-tile-click="getOrderDetails"
                        @update-orders="getDataUpdate"
                      />

                      <v-container
                        v-else
                        class="pa-6"
                        fluid
                      >
                        <v-row class="align-center justify-center">
                          <v-col
                            class="text-center grey--text"
                            cols="12"
                          >
                            <v-icon
                              class="mb-4"
                              color="grey lighten-2"
                              size="128"
                            >
                              {{ $t('Delivery.UserDashboard.Tab.Accepted.NoData.Icon') }}
                            </v-icon>
                            <div
                              class="text-h6 mb-2"
                              style="height: auto;"
                            >
                              {{ $t('Delivery.UserDashboard.Tab.Accepted.NoData.Title') }}
                            </div>
                            <div
                              class="text-subtitle-1"
                              style="height: auto;"
                            >
                              {{ $t('Delivery.UserDashboard.Tab.Accepted.NoData.Subtitle') }}
                            </div>
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-tab-item>

                    <v-tab-item
                      key="tab-received"
                      value="tab-received"
                    >
                      <delivery-user-orders-v-list
                        v-if="shiftOrdersReceived.length && tab === 'tab-received'"
                        :deliver-action="true"
                        :directions-action="true"
                        :generate-document-action="posUser.Settings.DeliveryStaff.Permissions.CanGenerateDocument"
                        :orders="shiftOrdersReceived"
                        :print-document-action="posUser.Settings.DeliveryStaff.Permissions.CanPrintDocument"
                        dense
                        @generate-document-action-click="generateDocument"
                        @print-document-action-click="printDocument"
                        @deliver-action-click="setShiftOrderStatus"
                        @directions-action-click="onRequestDirections"
                        @list-tile-click="getOrderDetails"
                        @update-orders="getDataUpdate"
                      />

                      <v-container
                        v-else
                        class="pa-6"
                        fluid
                      >
                        <v-row class="align-center justify-center">
                          <v-col
                            class="text-center grey--text"
                            cols="12"
                          >
                            <v-icon
                              class="mb-4"
                              color="grey lighten-2"
                              size="128"
                            >
                              {{ $t('Delivery.UserDashboard.Tab.Received.NoData.Icon') }}
                            </v-icon>
                            <div
                              class="text-h6 mb-2"
                              style="height: auto;"
                            >
                              {{ $t('Delivery.UserDashboard.Tab.Accepted.NoData.Title') }}
                            </div>
                            <div
                              class="text-subtitle-1"
                              style="height: auto;"
                            >
                              {{ $t('Delivery.UserDashboard.Tab.Accepted.NoData.Subtitle') }}
                            </div>
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-tab-item>

                    <v-tab-item
                      key="tab-completed"
                      value="tab-completed"
                    >
                      <delivery-user-orders-v-list
                        v-if="shiftOrdersCompleted.length && tab === 'tab-completed'"
                        :orders="shiftOrdersCompleted"
                        dense
                        @list-tile-click="getOrderDetails"
                        @update-orders="getDataUpdate"
                      />

                      <v-container
                        v-else
                        class="pa-6"
                        fluid
                      >
                        <v-row class="align-center justify-center">
                          <v-col
                            class="text-center grey--text"
                            cols="12"
                          >
                            <v-icon
                              class="mb-4"
                              color="grey lighten-2"
                              size="128"
                            >
                              {{ $t('Delivery.UserDashboard.Tab.Completed.NoData.Icon') }}
                            </v-icon>
                            <div
                              class="text-h6 mb-2"
                              style="height: auto;"
                            >
                              {{ $t('Delivery.UserDashboard.Tab.Completed.NoData.Title') }}
                            </div>
                            <div
                              class="text-subtitle-1"
                              style="height: auto;"
                            >
                              {{ $t('Delivery.UserDashboard.Tab.Completed.NoData.Subtitle') }}
                            </div>
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-tab-item>
                  </v-tabs-items>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>

          <v-card-text
            v-else
            class="pa-0"
          >
            <v-container
              class="pa-0 fill-height"
              fluid
            >
              <v-row dense>
                <v-col cols="12">
                  <v-tabs
                    v-model="tab"
                    grow
                    height="38"
                    show-arrows
                  >
                    <v-tab
                      class="px-2"
                      tab-value="tab-unpaid"
                      value="tab-unpaid"
                    >
                      <v-badge
                        :value="!!shiftOrdersUnPaid.length"
                        color="red"
                        inline
                        left
                      >
                        <template #badge>
                          {{ shiftOrdersUnPaid.length }}
                        </template>

                        <span :class="{'ml-1': !!shiftOrdersUnPaid.length}">
                          {{ $t('Delivery.UserDashboard.Tab.UnPaid.Title') }}
                        </span>
                      </v-badge>
                    </v-tab>

                    <v-tab
                      class="px-2"
                      tab-value="tab-paid"
                      value="tab-paid"
                    >
                      <v-badge
                        :value="!!shiftOrdersPaid.length"
                        color="orange"
                        inline
                        left
                      >
                        <template #badge>
                          {{ shiftOrdersPaid.length }}
                        </template>

                        <span :class="{'ml-1': !!shiftOrdersPaid.length}">
                          {{ $t('Delivery.UserDashboard.Tab.Paid.Title') }}
                        </span>
                      </v-badge>
                    </v-tab>
                  </v-tabs>
                </v-col>
              </v-row>

              <v-row
                class="pa-0 pb-2 overflow-x-hidden overflow-y-auto"
                no-gutters
                style="height: calc(100vh - 134px)"
              >
                <v-col cols="12">
                  <v-tabs-items
                    v-model="tab"
                    class="fill-height"
                  >
                    <v-tab-item
                      key="tab-unpaid"
                      value="tab-unpaid"
                    >
                      <delivery-user-orders-v-list
                        v-if="shiftOrdersUnPaid.length && tab === 'tab-unpaid'"
                        :orders="shiftOrdersUnPaid"
                        dense
                        @list-tile-click="getOrderDetails"
                        @update-orders="getDataUpdate"
                      />

                      <v-container
                        v-else
                        class="pa-6"
                        fluid
                      >
                        <v-row class="align-center justify-center">
                          <v-col
                            class="text-center grey--text"
                            cols="12"
                          >
                            <v-icon
                              class="mb-4"
                              color="grey lighten-2"
                              size="128"
                            >
                              {{ $t('Delivery.UserDashboard.Tab.UnPaid.NoData.Icon') }}
                            </v-icon>
                            <div
                              class="text-h6 mb-2"
                              style="height: auto;"
                            >
                              {{ $t('Delivery.UserDashboard.Tab.UnPaid.NoData.Title') }}
                            </div>
                            <div
                              class="text-subtitle-1"
                              style="height: auto;"
                            >
                              {{ $t('Delivery.UserDashboard.Tab.UnPaid.NoData.Subtitle') }}
                            </div>
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-tab-item>

                    <v-tab-item
                      key="tab-paid"
                      value="tab-paid"
                    >
                      <delivery-user-orders-v-list
                        v-if="shiftOrdersPaid.length && tab === 'tab-paid'"
                        :orders="shiftOrdersPaid"
                        dense
                        @list-tile-click="getOrderDetails"
                        @update-orders="getDataUpdate"
                      />

                      <v-container
                        v-else
                        class="pa-6"
                        fluid
                      >
                        <v-row class="align-center justify-center">
                          <v-col
                            class="text-center grey--text"
                            cols="12"
                          >
                            <v-icon
                              class="mb-4"
                              color="grey lighten-2"
                              size="128"
                            >
                              {{ $t('Delivery.UserDashboard.Tab.Paid.NoData.Icon') }}
                            </v-icon>
                            <div
                              class="text-h6 mb-2"
                              style="height: auto;"
                            >
                              {{ $t('Delivery.UserDashboard.Tab.Paid.NoData.Title') }}
                            </div>
                            <div
                              class="text-subtitle-1"
                              style="height: auto;"
                            >
                              {{ $t('Delivery.UserDashboard.Tab.Paid.NoData.Subtitle') }}
                            </div>
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-tab-item>
                  </v-tabs-items>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col
        v-if="!hasActiveShift"
        cols="12"
      >
        <v-container
          class="pa-0 fill-height"
          fluid
        >
          <v-row class="fill-height align-center justify-center">
            <v-col
              class="text-center grey--text"
              cols="12"
            >
              <v-icon
                class="mb-4"
                color="grey lighten-2"
                size="128"
              >
                {{ $t('Delivery.DeliveryStaffInfo.Result.InactiveShift.Icon') }}
              </v-icon>
              <div
                class="text-h6 mb-2"
                style="height: auto;"
              >
                {{ $t('Delivery.DeliveryStaffInfo.Result.InactiveShift.Title') }}
              </div>

              <div
                v-if="posUser.Settings.DeliveryStaff.Permissions.CanOpenShift"
                class="text-subtitle-1"
                style="height: auto;"
              >
                <v-btn
                  class="px-1 text-capitalize"
                  color="blue"
                  style="min-width: 0"
                  text
                  @click="onShiftOpenClick"
                >
                  {{ $t('Delivery.DeliveryStaffInfo.Result.InactiveShift.SubtitleLink') }}
                </v-btn>

                {{ $t('Delivery.DeliveryStaffInfo.Result.InactiveShift.SubtitleText') }}
              </div>

              <div
                v-else
                class="text-subtitle-1"
                style="height: auto;"
              >
                {{ $t('Delivery.DeliveryStaffInfo.Result.InactiveShift.SubtitleText2') }}
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-col>
    </v-row>

    <barcode-scanner-dialog
      v-if="barcodeScannerDialogVisible"
      :visible.sync="barcodeScannerDialogVisible"
      @barcodes="onBarcodesDetected"
    />

    <confirm-dialog
      :html-content="$t('Delivery.Button.RemoveOrder.PopUp.Content')"
      :html-title="$t('Delivery.Button.RemoveOrder.PopUp.Title')"
      :visible.sync="confirmDialogDeleteOrderVisible"
      no-button-class="v-btn--outline green--text"
      title-class="red white--text lighten-0"
      yes-button-class="red white--text"
      yes-button-event="delete-order-on-yes-btn"
      @delete-order-on-yes-btn="deleteOrder"
    />
  </v-container>
</template>

<script>
import AppData from '@/mixins/appdata'
import Auth from '@/mixins/auth'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import {clone} from '@/lib/helper/helper'
import AutoUpdateApiData from '@/mixins/api/autoUpdateApiData'
import GeoLocation from '@/mixins/geo/geoLocation'
import AutoGeoLocationTracking from '@/mixins/geo/autoGeoLocationTracking'
import DeliveryUserOrdersVList from '@/components/delivery/user/DeliveryUserOrdersVList'
import BarcodeScannerDialog from '@/components/delivery/user/BarcodeScannerDialog'
import BarcodePrefixEnum from '@/api/Enums/BarcodePrefixEnum'
import OrderStatusEnum from '@/api/Enums/OrderStatusEnum'
import PaymentTypeEnum from '@/api/Enums/PaymentTypeEnum'
import TransactionsCommon from '@/mixins/payments/transactionsCommon.js'

export default {
  name      : 'DeliveryUserDashboard',
  components: {
    BarcodeScannerDialog,
    DeliveryUserOrdersVList,
    ConfirmDialog
  },
  directives: {},
  mixins    : [AppData, Auth, AutoUpdateApiData, GeoLocation, AutoGeoLocationTracking, TransactionsCommon],
  props     : [],

  data() {
    return {
      tab                            : 'tab-added',
      viewData                       : null,
      selectedOrder                  : null,
      selectedOrderCode              : null,
      barcodeScannerDialogVisible    : false,
      confirmDialogDeleteOrderVisible: false,
      geoLocationOptions             : {
        autoStart: false,
        callback : 'setGeoLocationData'
      },
      autoUpdateApiDataOptions: {
        timeout  : 10 * 1000,
        autoStart: true,
        callback : 'getData'
      }
    }
  },

  computed: {
    OrderStatusEnum() {
      return OrderStatusEnum
    },
    deliveryStaffShift() {
      return this.viewData?.Shift || null
    },

    shiftOrders() {
      return this.deliveryStaffShift?.ShiftOrders || []
    },

    shiftTabOrdersForReceipt() {
      const tabOrders = this.tab === 'tab-accepted' ? this.shiftOrdersAccepted || [] : this.tab === 'tab-received' ? this.shiftOrdersReceived || [] : []

      return tabOrders.filter(order => !order.HasFiscalDocument && !order.IsGeneratingReceipt)
    },
    shiftTabOrdersForPrint() {
      const tabOrders = this.tab === 'tab-accepted' ? this.shiftOrdersAccepted || [] : this.tab === 'tab-received' ? this.shiftOrdersReceived || [] : []

      return tabOrders.filter(order => order.HasFiscalDocument && !order.IsGeneratingReceipt)
    },

    shiftOrdersAdded() {
      return this.shiftOrders.filter(order => order.DeliveryStaffAdded && !order.DeliveryStaffAccepted && !order.DeliveryStaffReceived && !order.DeliveryStaffDelivered)
    },

    shiftOrdersAccepted() {
      return this.shiftOrders.filter(order => order.DeliveryStaffAdded && order.DeliveryStaffAccepted && !order.DeliveryStaffReceived && !order.DeliveryStaffDelivered)
    },

    shiftOrdersReceived() {
      return this.shiftOrders.filter(order => order.DeliveryStaffAdded && order.DeliveryStaffAccepted && order.DeliveryStaffReceived && !order.DeliveryStaffDelivered)
    },

    shiftOrdersCompleted() {
      return this.shiftOrders.filter(order => order.DeliveryStaffCompleted && order.DeliveryStaffAdded && order.DeliveryStaffAccepted && order.DeliveryStaffReceived && order.DeliveryStaffDelivered)
    },

    shiftOrdersUnPaid() {
      const allowedPaymentTypes = PaymentTypeEnum.DELIVERY_STAFF_ORDER_PAYABLE
      return this.shiftOrders.filter(order => !order.DeliveryStaffCompleted && order.PaymentTransactions
        ?.some(transaction => (allowedPaymentTypes.includes(transaction.PaymentType) && (transaction.Paid !== 'success' || this.eftPosCardTransactionPaidSuccessful(transaction)))))
    },

    shiftOrdersPaid() {
      const allowedPaymentTypes = PaymentTypeEnum.ALL_TYPES
      return this.shiftOrders.filter(order => order.DeliveryStaffCompleted && order.PaymentTransactions
        ?.filter(transaction => allowedPaymentTypes.includes(transaction.PaymentType))
        ?.every(transaction => (allowedPaymentTypes.includes(transaction.PaymentType) && this.eftPosCardTransactionPaidSuccessful(transaction)) || transaction.PaymentType === PaymentTypeEnum.CASH)
      )
    },

    hasActiveShift() {
      return !!this.deliveryStaffShift?.HasActiveShift
    }
  },

  watch: {},

  created() {
    this.$bus.$on(window.SocketCommand.Order.GetDetails, this.onOrderDetailsResponse)
    this.$bus.$on(window.SocketCommand.Staff.Delivery.User.Shift, this.setData)
    this.$bus.$on(window.SocketCommand.Staff.Delivery.User.AddOrder, this.onOrderAdded)
    this.$bus.$on(window.SocketCommand.Staff.Delivery.User.RemoveOrder, this.getData)
    this.$bus.$on(window.SocketCommand.Staff.Delivery.User.ShiftOpen, this.getData)
    this.$bus.$on(window.SocketCommand.Staff.Delivery.User.ShiftClose, this.getData)
    this.$bus.$on(window.SocketCommand.Staff.Delivery.User.SetOrderStatus, this.getData)
    this.$bus.$on(window.SocketCommand.Staff.Delivery.User.ShiftUpdate, this.getData)
    this.$bus.$on(window.SocketCommand.Order.DeliveryNotify, this.onDeliveryNotifyResult)
    this.$bus.$on('update-delivery-user-shift-data', this.getData)

    if (this.posUser.Settings.DeliveryStaff.TrackLocation) {
      this.startAutoWatchGeoLocation()
    }

    this.getData()
  },

  mounted() {
  },

  beforeDestroy() {
    this.$bus.$off(window.SocketCommand.Order.GetDetails, this.onOrderDetailsResponse)
    this.$bus.$off(window.SocketCommand.Staff.Delivery.User.Shift, this.setData)
    this.$bus.$off(window.SocketCommand.Staff.Delivery.User.AddOrder, this.onOrderAdded)
    this.$bus.$off(window.SocketCommand.Staff.Delivery.User.RemoveOrder, this.getData)
    this.$bus.$off(window.SocketCommand.Staff.Delivery.User.ShiftOpen, this.getData)
    this.$bus.$off(window.SocketCommand.Staff.Delivery.User.ShiftClose, this.getData)
    this.$bus.$off(window.SocketCommand.Staff.Delivery.User.SetOrderStatus, this.getData)
    this.$bus.$off(window.SocketCommand.Staff.Delivery.User.ShiftUpdate, this.getData)
    this.$bus.$off(window.SocketCommand.Order.DeliveryNotify, this.onDeliveryNotifyResult)
    this.$bus.$off('update-delivery-user-shift-data', this.getData)
  },

  methods: {
    onDeliveryNotifyResult(data, payload) {
      this.$bus.$emit('app-show-notification', {
        body: this.$t('Delivery.UserDashboard.Message.Notify.Success', {orderCode: payload.OrderCode}),
        type: 'success',
        icon: 'info'
      })

      this.getData()
    },

    onRequestDirections(order) {
      if (!order?.Address?.LatLng) return

      const latLng = order.Address.LatLng
      const url = `https://www.google.com/maps/dir/?api=1&destination=${latLng}`
      window.open(url, '_blank')
    },

    showBarcodeScannerDialog() {
      this.barcodeScannerDialogVisible = true
    },

    showDeleteOrderDialog(order) {
      if (!order) return

      this.selectedOrder = order
      this.confirmDialogDeleteOrderVisible = true
    },
    deleteOrder() {
      if (!this.deliveryStaffShift || !this.selectedOrder) return
      const payload = clone(this.selectedOrder)
      window.callAS(window.SocketCommand.Staff.Delivery.User.RemoveOrder, {
        DeliveryStaffShiftId     : this.deliveryStaffShift.DeliveryStaffShiftId,
        DeliveryStaffShiftOrderId: payload.DeliveryStaffShiftOrderId,
        OrderId                  : payload.Id
      })
      this.selectedOrder = null
    },

    onAddOrderClick() {
      this.showBarcodeScannerDialog()
    },

    onBarcodesDetected(barcodes) {
      if (Array.isArray(barcodes) && (barcodes.length > 0) && this.deliveryStaffShift?.HasActiveShift) {
        const regex = new RegExp(`(${BarcodePrefixEnum.Order})([0-9]{1,})`, 'gi')
        barcodes.forEach((detectedBarcode) => {
          const [barcode, barcodeType, orderId] = regex.exec(detectedBarcode?.rawValue) || []
          if (barcode && barcodeType === BarcodePrefixEnum.Order && orderId) {
            window.callAS(window.SocketCommand.Staff.Delivery.User.AddOrder, {OrderId: orderId})
          }
        })
      }
    },

    getUserActivePrintMachineGroups() {
      const userCustomMachinesActive = this.posUser.Settings.AllUsers.ActivePrintMachineGroups.Active
      const userCustomMachines = this.posUser.Settings.AllUsers.ActivePrintMachineGroups.Items
      const hasUserCustomMachines = userCustomMachinesActive && Array.isArray(userCustomMachines) && userCustomMachines?.length > 0
      return hasUserCustomMachines ? userCustomMachines : null
    },

    generateDocument(order) {
      if (!order || !this.posUser.Settings.DeliveryStaff.Permissions.CanGenerateDocument) return

      this.resetAutoUpdateApiData()

      window.callAS(window.SocketCommand.Staff.Delivery.User.Receipt.Generate, {
        Id   : [parseInt(order.Id)],
        Guid : this.getUserActivePrintMachineGroups(),
        Print: this.posUser.Settings.DeliveryStaff.Permissions.CanPrintDocument
      })
    },
    generateAllDocuments() {
      if (!this.posUser.Settings.DeliveryStaff.Permissions.CanGenerateDocument || !this.shiftTabOrdersForReceipt.length) return

      const orders = this.shiftTabOrdersForReceipt.map(order => {
        this.$set(order, 'IsGeneratingReceipt', true)
        return parseInt(order.Id)
      })

      this.resetAutoUpdateApiData()

      window.callAS(window.SocketCommand.Staff.Delivery.User.Receipt.Generate, {
        Id   : orders,
        Guid : this.getUserActivePrintMachineGroups(),
        Print: this.posUser.Settings.DeliveryStaff.Permissions.CanPrintDocument
      })
    },

    resetAutoUpdateApiData(timeout = 8000) {
      this.stopAutoUpdateApiData()

      setTimeout(() => {
        this.startAutoUpdateApiData(this.getData)
        this.getData()
      }, timeout)
    },

    printDocument(order) {
      if (!order || !this.posUser.Settings.DeliveryStaff.Permissions.CanPrintDocument) return

      window.callAS(window.SocketCommand.Staff.Delivery.User.Receipt.Print, {
        Id  : parseInt(order.Id),
        Guid: this.getUserActivePrintMachineGroups()
      })
    },
    printAllDocuments() {
      //if (!this.posUser.Settings.DeliveryStaff.Permissions.CanPrintDocument || !this.shiftTabOrdersForPrint.length) return
      //const orders = this.shiftTabOrdersForPrint.map(order => {
      //  return parseInt(order.Id)
      //})
      //window.callAS(window.SocketCommand.Staff.Delivery.User.Receipt.Print, {Id: orders})
    },

    setShiftOrderStatus(order, statusId) {
      if (!order || !statusId) return
      window.callAS(window.SocketCommand.Staff.Delivery.User.SetOrderStatus, {
        OrderId : order.Id,
        StatusId: statusId
      })
    },

    onOrderAdded(data) {
      this.getData()
      this.$bus.$emit(window.SocketCommand.Staff.Delivery.Shift.AddOrder, data)
    },

    getOrderDetails(order) {
      this.selectedOrder = order
      window.callAS(window.SocketCommand.Order.GetDetails, {
        order_id : order.Id,
        status_id: order.StatusId
      })
    },
    onOrderDetailsResponse(data) {
      if (this.selectedOrder) {
        this.selectedOrder.Details = data
        const payload = clone(this.selectedOrder)
        this.selectedOrderCode = payload.OrderCode
        this.$bus.$emit('show-order-details-dialog', payload)
      }
      this.selectedOrder = null
    },

    getDeliveryStaffShiftTotals(withShiftStartMoney) {
      if (!this.deliveryStaff) return 0

      let i = 0
      let total = 0.00
      const orders = this.shiftOrders

      for (i = 0; i < orders.length; i++) {
        if (orders[i].Payment === PaymentTypeEnum.CASH && parseInt(orders[i].StatusId) !== 19) {
          total += parseFloat(orders[i].OrderTotal)
        }
      }

      if (withShiftStartMoney) total += parseFloat(this.deliveryStaffShift.ShiftStartMoney)

      return total
    },

    onShiftOpenClick() {
      window.callAS(window.SocketCommand.Staff.Delivery.User.ShiftOpen, {Money: 0})
    },

    onShiftCloseClick() {
      window.callAS(window.SocketCommand.Staff.Delivery.User.ShiftClose, {})
    },

    getDataUpdate() {
      window.callAS(window.SocketCommand.Staff.Delivery.User.Shift, {}, window.SocketCommand.Staff.Delivery.User.ShiftUpdate)
    },

    getData() {
      window.callAS(window.SocketCommand.Staff.Delivery.User.Shift)
    },
    setData(data) {
      this.viewData = data
      this.hideOrderDetails()

      this.$nextTick(() => {
        this.$forceUpdate()
      })
    },

    hideOrderDetails() {
      if (!this.selectedOrderCode) return

      const orderFound = this.shiftOrders?.find(order => order.OrderCode === this.selectedOrderCode)
      if (!orderFound) {
        this.$bus.$emit('hide-order-details-dialog')
        this.selectedOrderCode = null
      }
    }
  }
}
</script>

<style scoped>
:deep(.v-badge__wrapper) {
  margin: 0;
}

:deep(.v-tab) {
  min-width: 40px;
}
</style>
